/**
 * GeographyTab Component - Hierarchical Geographic Selection
 *
 * Allows contractors to manage the geographic jurisdictions they serve
 * with a guided, hierarchical approach:
 * 1. State (auto-detected from company location)
 * 2. Counties (searchable list)
 * 3. Cities/Townships (filtered by selected counties)
 *
 * Uses country-state-city package for up-to-date geographic data.
 * Debug logging enabled for troubleshooting save functionality.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { State, City } from 'country-state-city';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getCountiesByState } from '../../data/usCounties';
import Toast from '../Toast';
import InfoTooltip from '../InfoTooltip';

export default function GeographyTab() {
  const { contractor } = useAuth();
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Track initial load to prevent auto-save on mount
  const initialLoadComplete = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Step 1: State selection
  const [selectedState, setSelectedState] = useState('');
  const [stateData, setStateData] = useState(null);

  // Step 2: County selection (we'll use cities and extract unique counties)
  const [availableCounties, setAvailableCounties] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState(new Set());

  // Step 3: City selection (filtered by selected counties)
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState(new Set());

  // Search filters
  const [countySearch, setCountySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // Get US states
  const usStates = State.getStatesOfCountry('US');

  // Load jurisdictions from database
  useEffect(() => {
    if (contractor?.id) {
      loadJurisdictions();
    }
  }, [contractor?.id]);

  // Auto-lock state to contractor's office location (license enforcement)
  useEffect(() => {
    if (contractor?.office_state) {
      const stateCode = usStates.find(s => s.isoCode === contractor.office_state.toUpperCase())?.isoCode;
      if (stateCode && selectedState !== stateCode) {
        setSelectedState(stateCode);
      }
    }
  }, [contractor?.office_state, usStates, selectedState]);

  // Load cities and counties when state is selected
  useEffect(() => {
    if (selectedState) {
      const state = usStates.find(s => s.isoCode === selectedState);
      setStateData(state);

      // Get all cities for this state and filter out county names
      const allCities = City.getCitiesOfState('US', selectedState);

      // Filter out entries that are actually county names (contain "County" in the name)
      const actualCities = allCities.filter(city =>
        !city.name.toLowerCase().includes('county')
      );

      console.log(`Filtered ${allCities.length - actualCities.length} county entries from cities list`);

      // Sort cities alphabetically for better UX
      setAvailableCities(actualCities.sort((a, b) => a.name.localeCompare(b.name)));

      // Get all counties for this state from our static data
      const stateCounties = getCountiesByState(selectedState);
      setAvailableCounties(stateCounties);

      console.log(`Loaded ${stateCounties.length} counties for ${selectedState}`);
    } else {
      setStateData(null);
      setAvailableCounties([]);
      setAvailableCities([]);
    }
  }, [selectedState]);

  async function loadJurisdictions() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('geographic_jurisdictions')
        .select('*')
        .eq('contractor_id', contractor.id)
        .order('jurisdiction_type', { ascending: true })
        .order('jurisdiction_name', { ascending: true });

      if (error) throw error;

      setJurisdictions(data || []);

      // Pre-populate selections from database
      if (data && data.length > 0) {
        const state = data.find(j => j.jurisdiction_type === 'state');
        if (state) {
          const stateCode = usStates.find(s => s.name === state.jurisdiction_name)?.isoCode;
          if (stateCode) setSelectedState(stateCode);
        }

        const counties = data.filter(j => j.jurisdiction_type === 'county');
        setSelectedCounties(new Set(counties.map(c => c.jurisdiction_name)));

        // Filter cities and validate they don't contain "County" in the name
        const cities = data.filter(j =>
          j.jurisdiction_type === 'city' &&
          !j.jurisdiction_name.toLowerCase().includes('county')
        );
        setSelectedCities(new Set(cities.map(c => c.jurisdiction_name)));

        // Log warning if we found counties in the city list
        const misclassified = data.filter(j =>
          j.jurisdiction_type === 'city' &&
          j.jurisdiction_name.toLowerCase().includes('county')
        );
        if (misclassified.length > 0) {
          console.warn('Found counties misclassified as cities:', misclassified.map(m => m.jurisdiction_name));
        }
      }

      // Mark initial load as complete after a brief delay
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 300);
    } catch (err) {
      console.error('Error loading jurisdictions:', err);
      setError('Failed to load jurisdictions');
    } finally {
      setLoading(false);
    }
  }

  async function loadCountiesFromDatabase(stateCode) {
    try {
      const { data, error } = await supabase
        .from('geographic_jurisdictions')
        .select('jurisdiction_name')
        .eq('contractor_id', contractor.id)
        .eq('jurisdiction_type', 'county');

      if (error) throw error;

      const savedCounties = data?.map(c => c.jurisdiction_name) || [];

      // Get all counties for this state from our static data
      const stateCounties = getCountiesByState(stateCode);

      // Combine saved counties with state counties (in case user added custom ones)
      const allCounties = [...new Set([...stateCounties, ...savedCounties])].sort();
      setAvailableCounties(allCounties);

      console.log(`Loaded ${allCounties.length} counties for ${stateCode}`);
    } catch (err) {
      console.error('Error loading counties:', err);
    }
  }

  // Toggle county selection
  function toggleCounty(county) {
    const newSelected = new Set(selectedCounties);
    if (newSelected.has(county)) {
      newSelected.delete(county);
    } else {
      newSelected.add(county);
    }
    setSelectedCounties(newSelected);
  }

  // Toggle city selection
  function toggleCity(cityName) {
    const newSelected = new Set(selectedCities);
    if (newSelected.has(cityName)) {
      newSelected.delete(cityName);
    } else {
      newSelected.add(cityName);
    }
    setSelectedCities(newSelected);
  }

  // Select all filtered counties
  function selectAllCounties() {
    const newSelected = new Set(selectedCounties);
    filteredCounties.forEach(county => newSelected.add(county));
    setSelectedCounties(newSelected);
  }

  // Deselect all counties
  function deselectAllCounties() {
    setSelectedCounties(new Set());
  }

  // Select all filtered cities
  function selectAllCities() {
    const newSelected = new Set(selectedCities);
    filteredCities.forEach(city => newSelected.add(city.name));
    setSelectedCities(newSelected);
  }

  // Deselect all cities
  function deselectAllCities() {
    setSelectedCities(new Set());
  }

  // Add custom county
  function handleAddCustomCounty() {
    const countyName = prompt('Enter county name (e.g., "Orange County"):');
    if (countyName && countyName.trim()) {
      const trimmed = countyName.trim();
      setAvailableCounties(prev => [...new Set([...prev, trimmed])].sort());
      toggleCounty(trimmed);
    }
  }

  // Auto-save jurisdictions to database
  const autoSave = useCallback(async () => {
    if (!initialLoadComplete.current || !contractor?.id) return;
    if (!selectedState || selectedCounties.size === 0) return; // Don't save if invalid

    try {
      setSaving(true);
      console.log('Auto-saving...', { contractorId: contractor.id, selectedCounties: Array.from(selectedCounties) });

      // Delete all existing jurisdictions for this contractor
      const { error: deleteError } = await supabase
        .from('geographic_jurisdictions')
        .delete()
        .eq('contractor_id', contractor.id);

      if (deleteError) {
        console.error('Delete error:', deleteError);
        throw deleteError;
      }

      console.log('Existing jurisdictions deleted');

      // Prepare new jurisdictions
      const newJurisdictions = [];

      // Add state
      if (selectedState && stateData) {
        newJurisdictions.push({
          contractor_id: contractor.id,
          jurisdiction_type: 'state',
          jurisdiction_name: stateData.name,
          parent_jurisdiction: null,
          is_primary: true
        });
      }

      // Add counties
      selectedCounties.forEach(county => {
        newJurisdictions.push({
          contractor_id: contractor.id,
          jurisdiction_type: 'county',
          jurisdiction_name: county,
          parent_jurisdiction: stateData?.name || null,
          is_primary: false
        });
      });

      // Add cities (validate they're not counties)
      selectedCities.forEach(city => {
        // Skip if this looks like a county name
        if (city.toLowerCase().includes('county')) {
          console.warn(`Skipping "${city}" - appears to be a county, not a city`);
          return;
        }

        newJurisdictions.push({
          contractor_id: contractor.id,
          jurisdiction_type: 'city',
          jurisdiction_name: city,
          parent_jurisdiction: stateData?.name || null,
          is_primary: false
        });
      });

      // Insert new jurisdictions
      if (newJurisdictions.length > 0) {
        console.log('Inserting jurisdictions:', newJurisdictions);

        const { data: insertData, error: insertError } = await supabase
          .from('geographic_jurisdictions')
          .insert(newJurisdictions);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }

        console.log('Insert successful:', insertData);
      }

      // Don't reload jurisdictions - would cause infinite loop by updating state
      // The UI already reflects the current selections

      // Show toast notification
      setToastMessage('Service areas saved');
      setToastType('success');
      setShowToast(true);
    } catch (err) {
      console.error('Error saving jurisdictions:', err);
      setToastMessage('Failed to save service areas');
      setToastType('error');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  }, [contractor, selectedState, stateData, selectedCounties, selectedCities]);

  // Debounced auto-save effect - triggers 500ms after user stops selecting
  useEffect(() => {
    if (!initialLoadComplete.current) return;
    if (!selectedState || selectedCounties.size === 0) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 500);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounties, selectedCities, selectedState]);

  // Filter counties by search
  const filteredCounties = availableCounties.filter(county =>
    county.toLowerCase().includes(countySearch.toLowerCase())
  );

  // Filter cities by search and selected counties
  const filteredCities = availableCities.filter(city => {
    const matchesSearch = city.name.toLowerCase().includes(citySearch.toLowerCase());
    // If no counties selected, show all cities. Otherwise, filter by county association
    // (Note: This is simplified - in production you'd have county-city relationships)
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '16px', color: '#64748b' }}>Loading service areas...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {success && (
        <div style={{
          padding: '15px',
          background: '#dcfce7',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          color: '#14532d',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span>✅</span>
          <strong>Service areas saved successfully!</strong>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '15px',
          background: '#fee2e2',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Info Card */}
      <div style={{
        padding: '15px',
        background: '#f0f9ff',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#0c4a6e',
        lineHeight: '1.6'
      }}>
        <strong>💡 How it works:</strong> Your service state is locked to your company's registered address.
        Choose the counties you serve, then add specific cities within those counties. This helps filter rebates
        to show only those available in your customer's exact location.
      </div>

      {/* Main Card */}
      <div className="card">
        <h2 style={{ fontSize: '20px', marginBottom: '20px' }}>📍 Service Area Configuration</h2>

        {/* Step 1: State (Locked) */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '12px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: selectedState ? '#22c55e' : '#94a3b8',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              {selectedState ? '✓' : '1'}
            </div>
            <h3 style={{ fontSize: '18px', margin: 0, fontWeight: '600' }}>
              Your Licensed State
            </h3>
            <div style={{
              padding: '3px 8px',
              background: '#fef3c7',
              color: '#92400e',
              fontSize: '11px',
              fontWeight: '600',
              borderRadius: '4px',
              border: '1px solid #fbbf24',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🔒 LOCKED
              <InfoTooltip text={`Your license is limited to ${stateData?.name || contractor?.office_state || 'one state'}. To serve multiple states, each location requires a separate license. Update your company address in Settings → Company Info to change your service state.`} />
            </div>
          </div>

          <div style={{ marginLeft: '42px' }}>
            {selectedState && stateData ? (
              <div style={{
                padding: '16px',
                background: '#f8fafc',
                border: '2px solid #e0e6ed',
                borderRadius: '8px',
                maxWidth: '400px'
              }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#2c3e50',
                  marginBottom: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '24px' }}>🏛️</span>
                  {stateData.name}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  Locked to your company's registered state
                </div>
              </div>
            ) : (
              <div style={{
                padding: '16px',
                background: '#fee2e2',
                border: '2px solid #ef4444',
                borderRadius: '8px',
                maxWidth: '400px',
                color: '#dc2626'
              }}>
                <strong>⚠️ No state configured</strong>
                <div style={{ fontSize: '13px', marginTop: '6px' }}>
                  Please set your company address in Settings → Company Info
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: County Selection */}
        {selectedState && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: selectedCounties.size > 0 ? '#22c55e' : '#667eea',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {selectedCounties.size > 0 ? '✓' : '2'}
              </div>
              <h3 style={{ fontSize: '18px', margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                Select Counties You Serve
                <InfoTooltip text="Selecting the counties you serve helps us show only relevant county-wide rebates and incentives available to your customers in those areas." />
              </h3>
              <div style={{
                padding: '4px 10px',
                background: '#f0f9ff',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#0c4a6e'
              }}>
                {selectedCounties.size} selected
              </div>
            </div>

            <div style={{ marginLeft: '42px' }}>
              {/* Search + Bulk Actions */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="🔍 Search counties..."
                    value={countySearch}
                    onChange={(e) => setCountySearch(e.target.value)}
                    style={{ flex: 1, maxWidth: '400px' }}
                  />
                </div>

                {/* Bulk Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={selectAllCounties}
                    style={{
                      padding: '6px 12px',
                      background: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '6px',
                      color: '#0c4a6e',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✓ Select All {countySearch && `(${filteredCounties.length})`}
                  </button>
                  <button
                    type="button"
                    onClick={deselectAllCounties}
                    style={{
                      padding: '6px 12px',
                      background: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      color: '#92400e',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Clear All
                  </button>
                  <button
                    type="button"
                    onClick={handleAddCustomCounty}
                    style={{
                      padding: '6px 12px',
                      background: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      color: '#475569',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Custom
                  </button>
                </div>
              </div>

              {/* County List */}
              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '2px solid #e0e6ed',
                borderRadius: '8px',
                padding: '12px',
                background: '#f8fafc'
              }}>
                {filteredCounties.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    {countySearch ? `No counties found matching "${countySearch}"` : 'No counties available. Click "+ Add Custom" to add one.'}
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '8px'
                  }}>
                    {filteredCounties.map(county => (
                      <label
                        key={county}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          background: selectedCounties.has(county) ? '#dcfce7' : 'white',
                          border: `1px solid ${selectedCounties.has(county) ? '#22c55e' : '#cbd5e1'}`,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          fontSize: '14px',
                          fontWeight: selectedCounties.has(county) ? '600' : '500'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedCounties.has(county)) {
                            e.currentTarget.style.background = '#f0f9ff';
                            e.currentTarget.style.borderColor = '#0ea5e9';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedCounties.has(county)) {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#cbd5e1';
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCounties.has(county)}
                          onChange={() => toggleCounty(county)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {county.replace(' County', '')}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: City Selection */}
        {selectedState && selectedCounties.size > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: selectedCities.size > 0 ? '#22c55e' : '#667eea',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                {selectedCities.size > 0 ? '✓' : '3'}
              </div>
              <h3 style={{ fontSize: '18px', margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                Select Cities You Serve
                <InfoTooltip text="Adding specific cities allows you to target city-specific rebates and customize assessments based on your customer's exact location within the county." />
              </h3>
              <div style={{
                padding: '4px 10px',
                background: '#f0f9ff',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#0c4a6e'
              }}>
                {selectedCities.size} selected
              </div>
            </div>

            <div style={{ marginLeft: '42px' }}>
              {/* Search + Bulk Actions */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="🔍 Search cities..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    style={{ flex: 1, maxWidth: '400px' }}
                  />
                </div>

                {/* Bulk Action Buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={selectAllCities}
                    style={{
                      padding: '6px 12px',
                      background: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '6px',
                      color: '#0c4a6e',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✓ Select All {citySearch && `(${filteredCities.length})`}
                  </button>
                  <button
                    type="button"
                    onClick={deselectAllCities}
                    style={{
                      padding: '6px 12px',
                      background: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      color: '#92400e',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Clear All
                  </button>
                  {filteredCities.length > 50 && (
                    <span style={{
                      fontSize: '12px',
                      color: '#64748b',
                      fontStyle: 'italic'
                    }}>
                      💡 Tip: Use search to narrow down results
                    </span>
                  )}
                </div>
              </div>

              {/* City List */}
              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '2px solid #e0e6ed',
                borderRadius: '8px',
                padding: '12px',
                background: '#f8fafc'
              }}>
                {filteredCities.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                    {citySearch ? `No cities found matching "${citySearch}"` : 'No cities available'}
                  </div>
                ) : (
                  <>
                    {filteredCities.length > 100 && (
                      <div style={{
                        padding: '12px',
                        background: '#fffbeb',
                        border: '1px solid #fbbf24',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#92400e',
                        marginBottom: '12px'
                      }}>
                        ⚡ Showing {filteredCities.length} cities. Use search to filter or "Select All" to add them quickly.
                      </div>
                    )}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: '6px'
                    }}>
                      {filteredCities.map(city => (
                        <label
                          key={city.name}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 8px',
                            background: selectedCities.has(city.name) ? '#dcfce7' : 'white',
                            border: `1px solid ${selectedCities.has(city.name) ? '#22c55e' : '#cbd5e1'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: selectedCities.has(city.name) ? '600' : '500',
                            transition: 'all 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedCities.has(city.name)) {
                              e.currentTarget.style.background = '#f0f9ff';
                              e.currentTarget.style.borderColor = '#0ea5e9';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!selectedCities.has(city.name)) {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCities.has(city.name)}
                            onChange={() => toggleCity(city.name)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {city.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        {selectedState && (
          <div style={{
            marginTop: '30px',
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '8px'
          }}>
            <h4 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: '600' }}>
              📋 Summary
            </h4>
            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.8' }}>
              <div><strong>State:</strong> {stateData?.name || 'Not selected'}</div>
              <div><strong>Counties:</strong> {selectedCounties.size} selected</div>
              <div><strong>Cities:</strong> {selectedCities.size > 0 ? `${selectedCities.size} selected` : <span style={{ color: '#f59e0b' }}>None selected - consider adding cities for better rebate targeting</span>}</div>
            </div>

            {saving && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f0f9ff',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#0c4a6e',
                textAlign: 'center'
              }}>
                💾 Saving changes...
              </div>
            )}

            {selectedCounties.size === 0 && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#dc2626' }}>
                Please select at least one county
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}
